import React, { useState, useEffect } from 'react';
import { companyService, authService, referenceService, warehouseService } from '../../services/api';
import { salesService } from '../../services/salesService';
import axios from 'axios';
import { createPortal } from 'react-dom';
import Swal from 'sweetalert2';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';


const DeliveryChallanManagement = () => {
    const [dcs, setDcs] = useState([]);
    const [quotations, setQuotations] = useState([]);
    const [organizations, setOrganizations] = useState([]); // Added state
    const [warehouses, setWarehouses] = useState([]); // Added warehouses state

    // Force SweetAlert to be on top of everything
    useEffect(() => {
        const style = document.createElement('style');
        style.innerHTML = `
            .swal2-container {
                z-index: 10000 !important;
            }
        `;
        document.head.appendChild(style);
        return () => {
            document.head.removeChild(style);
        };
    }, []);
    const [searchTerm, setSearchTerm] = useState(''); // Added search state
    const [showModal, setShowModal] = useState(false);
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [editingDC, setEditingDC] = useState(null);
    const [selectedQuote, setSelectedQuote] = useState(null);
    const [selectedDCForStatus, setSelectedDCForStatus] = useState(null);
    const [newStatus, setNewStatus] = useState('');
    const [quoteItems, setQuoteItems] = useState([]);
    const [errors, setErrors] = useState({}); // Added errors state
    const [loading, setLoading] = useState(true);
    const [formData, setFormData] = useState({
        quotationId: '',
        deliveryDate: new Date().toISOString().split('T')[0],
        receiverName: '',
        location: '',
        location: '',
        items: [] // { quotationItemId, quantity, fromWarehouse }
    });

    useEffect(() => {
        fetchDcs();
        fetchQuotations();
        fetchOrganizations(); // Added call
        fetchDcs();
        fetchQuotations();
        fetchOrganizations(); // Added call
        fetchWarehouses();
    }, []);

    const fetchWarehouses = async () => {
        try {
            const res = await warehouseService.getAll();
            const sorted = (res.data || []).sort((a, b) => a.name.localeCompare(b.name));
            setWarehouses(sorted);
        } catch (err) {
            console.error('Error fetching warehouses', err);
        }
    };

    const fetchOrganizations = async () => {
        try {
            const res = await referenceService.getOrgs();
            setOrganizations(res.data);
        } catch (err) {
            console.error('Error fetching Organizations', err);
        }
    };

    const fetchDcs = async () => {
        try {
            const res = await salesService.getDeliveryChallans();
            setDcs(res.data);
            setLoading(false);
        } catch (err) {
            console.error('Error fetching DCs', err);
            setLoading(false);
        }
    };

    const fetchQuotations = async () => {
        try {
            const token = localStorage.getItem('rsms_token');
            const res = await axios.get('/api/quotations', { headers: { Authorization: `Bearer ${token}` } });
            const awarded = res.data.filter(q =>
                (q.status && q.status.toLowerCase() === 'awarded') ||
                (q.status && q.status === 'Approved/Won') ||
                parseInt(q.currentStage) === 3 ||
                (q.stageName && q.stageName.toLowerCase() === 'awarded')
            );
            console.log('Fetched Awarded Quotations:', awarded);
            setQuotations(awarded);
        } catch (err) {
            console.error('Error fetching Quotations', err);
        }
    };

    const handleQuoteSelect = (e) => {
        const quoteId = e.target.value;
        const quote = quotations.find(q => q.id === parseInt(quoteId));

        // Fetch already delivered quantities for this Quote
        // Ideally we should have an API for this, or derive it from fetched DCs.
        // For now, let's look at locally fetched 'dcs'. 
        // NOTE: This might be incomplete if pagination existed, but assuming full list for now.
        const alreadyDeliveredMap = new Map(); // QuoteItemId -> TotalDelivered

        // Filter DCs for this quote
        const relatedDCs = dcs.filter(d => d.QuotationId === parseInt(quoteId));
        relatedDCs.forEach(d => {
            d.DeliveryChallanItems.forEach(dci => {
                const current = alreadyDeliveredMap.get(dci.QuotationItemId) || 0;
                alreadyDeliveredMap.set(dci.QuotationItemId, current + dci.quantity);
            });
        });


        const preparedItems = (quote.QuotationItems || []).map(item => {
            const delivered = alreadyDeliveredMap.get(item.id) || 0;
            const remaining = item.quantity - delivered;

            return {
                ...item,
                alreadyDelivered: delivered,
                remainingQty: remaining,
                deliverQty: 0,
                selectedWarehouse: '' // Force user to select warehouse
            };
        });

        // Auto-Fill Location logic
        console.log('Selected Quote:', quote);
        console.log('Quote Organization:', quote?.Organization);

        let autoLocation = '';
        let orgData = quote?.Organization;

        // Fallback: If no related Organization object, find by string name
        if (!orgData && quote && quote.organization) {
            console.log(`Trying fallback lookup for organization name: "${quote.organization}"`);
            const foundOrg = organizations.find(o =>
                o.name.toLowerCase().trim() === quote.organization.toLowerCase().trim()
            );
            if (foundOrg) {
                console.log('Fallback Organization Found:', foundOrg);
                orgData = foundOrg;
            }
        }

        if (orgData) {
            autoLocation = orgData.address;

            // Try Location field if address is empty
            if (!autoLocation) {
                autoLocation = orgData.location;
            }

            if (!autoLocation) {
                const parts = [orgData.city, orgData.country].filter(Boolean);
                if (parts.length > 0) autoLocation = parts.join(', ');
            }
        }
        console.log('Determined Auto Location:', autoLocation);

        setFormData(prev => ({
            ...prev,
            quotationId: quoteId,
            ...prev,
            quotationId: quoteId,
            location: autoLocation || prev.location, // Fill if found, else keep existing or empty
            receiverName: quote?.Organization?.contactPerson || quote?.toContact || ''
        }));

        setSelectedQuote(quote);
        if (quote) {
            setQuoteItems(preparedItems);
        } else {
            setQuoteItems([]);
        }
    };

    const handleQtyChange = (itemId, qty) => {
        setQuoteItems(prev => prev.map(item =>
            item.id === itemId ? { ...item, deliverQty: parseInt(qty) || 0 } : item
        ));
    };

    const handleWarehouseChange = (itemId, val) => {
        setQuoteItems(prev => prev.map(item =>
            item.id === itemId ? { ...item, selectedWarehouse: val } : item
        ));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.quotationId) return alert('Select Quotation');

        const itemsToDeliver = quoteItems
            .filter(i => i.deliverQty > 0)
            .map(i => ({
                quotationItemId: i.id,
                quantity: i.deliverQty,
                fromWarehouse: i.selectedWarehouse
            }));

        if (itemsToDeliver.length === 0) {
            Swal.fire('Validation Error', 'Enter quantity for at least one item', 'warning');
            return;
        }

        // Validate Warehouse Selection & Stock Limits
        const newErrors = {};
        let hasErrors = false;

        for (const item of itemsToDeliver) {
            // Check Warehouse Selection
            if (!item.fromWarehouse) {
                newErrors[item.quotationItemId] = 'Please select a warehouse';
                hasErrors = true;
            } else {
                // Check Stock Availability if warehouse is selected
                // Find the original item in quoteItems to access Product data
                const originalItem = quoteItems.find(qi => qi.id === item.quotationItemId);
                if (originalItem && originalItem.Product && originalItem.Product.ProductStocks) {
                    const stockEntry = originalItem.Product.ProductStocks.find(s => s.WarehouseId == item.fromWarehouse);
                    const availableStock = stockEntry ? stockEntry.quantity : 0;

                    if (item.quantity > availableStock) {
                        newErrors[item.quotationItemId] = `Insufficient Stock (Available: ${availableStock})`;
                        hasErrors = true;
                    }
                }
            }

            // Check Remaining Limit
            const originalItem = quoteItems.find(qi => qi.id === item.quotationItemId);
            if (originalItem && item.quantity > originalItem.remainingQty) {
                newErrors[item.quotationItemId] = `Exceeds remaining quantity (${originalItem.remainingQty})`;
                hasErrors = true;
            }
        }

        if (hasErrors) {
            setErrors(newErrors);
            Swal.fire('Validation Error', 'Please correct the errors highlighted in the form.', 'error');
            return; // Stop submission
        }

        setErrors({}); // Clear errors checks passed

        const payload = {
            quotationId: formData.quotationId,
            deliveryDate: formData.deliveryDate,
            receiverName: formData.receiverName,
            location: formData.location,
            items: itemsToDeliver
        };

        try {
            if (editingDC) {
                // Update mode
                await salesService.updateDeliveryChallan(editingDC.id, {
                    deliveryDate: formData.deliveryDate,
                    receiverName: formData.receiverName,
                    location: formData.location,
                    items: itemsToDeliver // Send updated items
                });
                await fetchDcs(); // Refresh Data immediately
                setShowModal(false);
                await Swal.fire('Success', 'Delivery Challan Updated!', 'success');
            } else {
                // Create mode
                await salesService.createDeliveryChallan(payload);
                await fetchDcs(); // Refresh Data immediately
                setShowModal(false);
                await Swal.fire({
                    title: 'Success!',
                    text: 'Delivery Challan Created Successfully!',
                    icon: 'success',
                    confirmButtonColor: '#6f42c1'
                });
            }

            setEditingDC(null); // Reset edit mode
            setFormData({
                quotationId: '',
                deliveryDate: new Date().toISOString().split('T')[0],
                receiverName: '',
                location: '',
                items: []
            });
            setSelectedQuote(null);
            setQuoteItems([]);
        } catch (err) {
            console.error(err);
            // DO NOT close modal on error
            // Show cleaner error message from backend if available
            const errorMsg = err.response?.data?.error || err.message;
            Swal.fire('Error', errorMsg, 'error');
        }
    };

    const handleEdit = (dc) => {
        setEditingDC(dc);
        setFormData({
            quotationId: dc.QuotationId,
            deliveryDate: dc.deliveryDate,
            receiverName: dc.receiverName,
            location: dc.location || '',
            items: []
        });

        const q = quotations.find(q => q.id === dc.QuotationId);
        setSelectedQuote(q);

        if (q) {
            // Calculate delivered quantities by OTHER DCs (excluding current one being edited)
            const otherDCs = dcs.filter(d => d.QuotationId === dc.QuotationId && d.id !== dc.id);
            const otherDeliveredMap = new Map();
            otherDCs.forEach(d => {
                d.DeliveryChallanItems?.forEach(dci => {
                    const current = otherDeliveredMap.get(dci.QuotationItemId) || 0;
                    otherDeliveredMap.set(dci.QuotationItemId, current + dci.quantity);
                });
            });

            // Current DC items map
            const currentDCItemsMap = new Map();
            if (dc.DeliveryChallanItems) {
                dc.DeliveryChallanItems.forEach(dci => {
                    currentDCItemsMap.set(dci.QuotationItemId, {
                        qty: dci.quantity,
                        warehouseId: dci.fromWarehouse
                    });
                });
            }

            const preparedItems = q.QuotationItems.map(item => {
                const deliveredByOthers = otherDeliveredMap.get(item.id) || 0;
                // Remaining for this DC = Total - deliveredByOthers
                const remaining = item.quantity - deliveredByOthers;

                const currentDCItem = currentDCItemsMap.get(item.id);
                const currentDeliverQty = currentDCItem ? currentDCItem.qty : 0;
                const currentWarehouse = currentDCItem ? currentDCItem.warehouseId : '';

                return {
                    ...item,
                    alreadyDelivered: deliveredByOthers,
                    remainingQty: remaining, // This is the limit for this DC (includes what we are editing)
                    deliverQty: currentDeliverQty,
                    selectedWarehouse: currentWarehouse
                };
            });

            setQuoteItems(preparedItems);
        } else {
            console.warn("Linked quotation not found in fetched list.");
            setQuoteItems([]);
        }

        setShowModal(true);
    };



    const handleStatusClick = (dc) => {
        setSelectedDCForStatus(dc);
        setNewStatus(dc.status);
        setShowStatusModal(true);
    };

    const saveStatusUpdate = async () => {
        try {
            await salesService.updateDCStatus(selectedDCForStatus.id, newStatus);
            setShowStatusModal(false); // Close modal first
            await Swal.fire('Success', 'Status Updated', 'success');
            fetchDcs();
        } catch (error) {
            console.error("Status Update Failed", error);
            setShowStatusModal(false); // Close modal on error too
            const errorMsg = error.response?.data?.error || error.message || 'Failed to update status';
            Swal.fire('Error', errorMsg, 'error');
        }
    };

    const handleDelete = async (id) => {
        try {
            const result = await Swal.fire({
                title: 'Are you sure?',
                text: "You won't be able to revert this!",
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#3085d6',
                confirmButtonText: 'Yes, delete it!'
            });

            if (result.isConfirmed) {
                await salesService.deleteDeliveryChallan(id);
                await Swal.fire('Deleted!', 'Delivery Challan has been deleted.', 'success');
                fetchDcs(); // Refresh list
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Cannot Delete DC',
                text: error.response?.data?.error || 'Failed to delete Delivery Challan.',
            });
        }
    };

    const handleCreate = () => {
        setEditingDC(null);
        setFormData({
            quotationId: '',
            deliveryDate: new Date().toISOString().split('T')[0],
            receiverName: '',
            location: '',
            items: []
        });
        setSelectedQuote(null);
        setQuoteItems([]);
        setErrors({});
        setShowModal(true);
    };

    const generatePDF = async (dc) => {
        try {
            const companyRes = await companyService.getProfile();
            const company = companyRes.data || {};

            // Explicit A4 Format
            const doc = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'a4'
            });
            const pageWidth = doc.internal.pageSize.width;

            let yPos = 15;

            // --- Header Section (Logos & Company Info) ---
            // Logo (Left aligned)
            if (company.logo && company.logo.startsWith('data:image')) {
                try {
                    const format = company.logo.includes('image/jpeg') ? 'JPEG' : 'PNG';
                    doc.addImage(company.logo, format, 15, yPos, 40, 20); // Adjust size as needed
                } catch (err) { console.error("Logo error:", err); }
            }

            // Distributor Logos (Right aligned)
            let activeLogos = [];
            try {
                // Use DC's Quotation preference
                const pref = dc.Quotation?.distributorLogo || 'default';
                let potentialLogos = [];

                if (company.distributorLogos) {
                    const parsed = JSON.parse(company.distributorLogos);
                    if (Array.isArray(parsed)) {
                        potentialLogos = parsed;
                    }
                }
                // Fallback legacy
                if (potentialLogos.length === 0 && company.distributorLogo) {
                    potentialLogos.push({ src: company.distributorLogo, active: true });
                }

                if (pref === 'none') {
                    activeLogos = [];
                } else if (pref === 'default' || !pref) {
                    activeLogos = potentialLogos.filter(l => l.active);
                } else if (pref.startsWith('logo_')) {
                    const index = parseInt(pref.replace('logo_', ''));
                    if (potentialLogos[index]) {
                        activeLogos = [potentialLogos[index]];
                    }
                }
            } catch (e) {
                console.error("Error parsing distributorLogos", e);
            }

            // Render Logos
            if (activeLogos.length > 0) {
                // Settings for logo row
                const logoWidth = 35;
                const logoHeight = 15;
                const spacing = 2;
                let startX = pageWidth - 15 - (activeLogos.length * logoWidth) - ((activeLogos.length - 1) * spacing);

                // If too many, just stack from right? Or simpler: Just align them to the right.
                // Let's align them to the right edge.

                // Better approach: align rightmost logo to right margin, others to its left.
                const rightMargin = pageWidth - 15;

                activeLogos.reverse().forEach((logo, index) => { // Reverse to render right-to-left
                    if (logo.src && logo.src.startsWith('data:image')) {
                        try {
                            const format = logo.src.includes('image/jpeg') ? 'JPEG' : 'PNG';
                            const xPos = rightMargin - ((index + 1) * logoWidth) - (index * spacing);
                            doc.addImage(logo.src, format, xPos, yPos, logoWidth, logoHeight);
                        } catch (err) { console.error("Logo render error", err); }
                    }
                });
            }

            let addrY = yPos + 8;

            // Company Name (Larger & Bold)
            doc.setFontSize(18); // Increased size
            doc.setFont(undefined, 'bold');
            doc.setTextColor(44, 62, 80); // Dark Blue/Grey
            doc.text(company.name || 'World Wide Vision Pvt Ltd', pageWidth / 2, addrY, { align: 'center' });

            addrY += 6;

            // Address Details (Normal)
            doc.setFontSize(9);
            doc.setFont(undefined, 'normal');
            doc.setTextColor(100); // Dark Grey

            const contactLine = [
                company.phone ? `Tel: ${company.phone}` : '',
                company.email ? `E-mail: ${company.email}` : '',
                company.website ? `${company.website}` : ''
            ].filter(Boolean).join(' | ');

            const addressText = company.address || '';

            doc.text(addressText, pageWidth / 2, addrY, { align: 'center' });
            addrY += 5;
            doc.text(contactLine, pageWidth / 2, addrY, { align: 'center' });
            addrY += 5;

            yPos = addrY + 10;

            // --- Client Details & DC Info (Side by Side Tables) ---

            doc.autoTable = autoTable; // Or use autoTable(doc, ...) directly.

            // Client Details Table (Left)
            const clientHeaders = [[{ content: 'Client Details:', colSpan: 2, styles: { halign: 'center' } }]];

            // Client Details Fetching with Fallbacks
            const attention = dc.Quotation?.toContact || dc.receiverName || '-';
            const organizationName = dc.Quotation?.Organization?.name || dc.Quotation?.organization || dc.location || '-';

            // Determine Location Fallback
            let displayedLocation = dc.location;
            if (!displayedLocation || displayedLocation === '-') {
                // Try via Quotation -> Organization
                displayedLocation = dc.Quotation?.Organization?.address || dc.Quotation?.Organization?.location;

                // Try direct Organization
                if (!displayedLocation) {
                    displayedLocation = dc.Organization?.address || dc.Organization?.location;
                }

                // Try fallback parts
                if (!displayedLocation) {
                    const org = dc.Quotation?.Organization || dc.Organization;
                    if (org) {
                        const parts = [org.city, org.country].filter(Boolean);
                        if (parts.length > 0) displayedLocation = parts.join(', ');
                    }
                }
            }
            const locationValue = displayedLocation || '-';

            const clientData = [
                ['Attention:', attention],
                ['Organization:', organizationName],
                ['Location:', locationValue]
            ];

            autoTable(doc, {
                startY: yPos,
                head: clientHeaders,
                body: clientData,
                theme: 'plain',
                tableWidth: 90,
                margin: { left: 15 },
                headStyles: {
                    fillColor: [200, 200, 200],
                    textColor: 0,
                    fontStyle: 'bold',
                    halign: 'left',
                    lineWidth: 0.1,
                    lineColor: 0
                },
                bodyStyles: {
                    lineWidth: 0.1,
                    lineColor: 0,
                    cellPadding: 1
                },
                columnStyles: {
                    0: { cellWidth: 25, fontStyle: 'bold' },
                    1: { cellWidth: 65 }
                }
            });

            let leftTableFinalY = doc.lastAutoTable.finalY;

            // Date & DC# Table (Right)
            const dateHeaders = [['DATE', 'DC #']];
            const dateData = [[dc.deliveryDate, dc.dcNumber]];

            autoTable(doc, {
                startY: yPos,
                head: dateHeaders,
                body: dateData,
                theme: 'plain',
                tableWidth: 50,
                margin: { left: pageWidth - 65 },
                headStyles: {
                    fillColor: [200, 200, 200],
                    textColor: 0,
                    fontStyle: 'bold',
                    halign: 'center',
                    lineWidth: 0.1,
                    lineColor: 0
                },
                bodyStyles: {
                    halign: 'center',
                    lineWidth: 0.1,
                    lineColor: 0,
                    cellPadding: 1
                }
            });

            let rightTableFinalY = doc.lastAutoTable.finalY;
            yPos = Math.max(leftTableFinalY, rightTableFinalY) + 10;

            // --- Title ---
            doc.setFontSize(16);
            doc.setFont(undefined, 'bold');
            doc.text('DELIVERY CHALLAN', pageWidth / 2, yPos, { align: 'center' });
            doc.line(pageWidth / 2 - 30, yPos + 2, pageWidth / 2 + 30, yPos + 2);

            yPos += 10;

            // --- Items Table ---
            const itemColumns = ["SR. NO.", "MODEL NO.", "DESCRIPTION", "QTY"];
            const itemRows = [];

            if (dc.DeliveryChallanItems) {
                dc.DeliveryChallanItems.forEach((item, index) => {
                    const modelNo = item.Product?.modelNumber || item.QuotationItem?.modelNumber || '-';
                    const desc = item.description || item.QuotationItem?.description || '-';

                    itemRows.push([
                        index + 1,
                        modelNo,
                        desc,
                        item.quantity
                    ]);
                });
            }

            autoTable(doc, {
                startY: yPos,
                head: [itemColumns],
                body: itemRows,
                theme: 'grid',
                headStyles: {
                    fillColor: [44, 62, 80], // Dark Blue header matching Quotation
                    textColor: 255,
                    fontStyle: 'bold',
                    halign: 'center',
                    lineColor: 0,
                    lineWidth: 0.1
                },
                styles: {
                    lineColor: 0,
                    lineWidth: 0.1,
                    textColor: 0,
                    halign: 'center' // Default center alignment for this specific table style
                },
                columnStyles: {
                    0: { cellWidth: 15, halign: 'center' }, // SR. NO. - Minimized
                    1: { cellWidth: 35, halign: 'center' }, // MODEL NO.
                    2: { halign: 'left' },                  // DESCRIPTION - Auto/Maximized
                    3: { cellWidth: 20, halign: 'center' }  // QTY
                }
            });

            // --- Footer ---
            let finalY = doc.lastAutoTable.finalY + 10;

            doc.setFontSize(9);
            doc.setFont(undefined, 'bold');
            doc.text('I Certify that I have received and accepted the above items in Brand New & Good condition.', 15, finalY);

            finalY += 20;

            // Signatures
            doc.text('Delivered by :', 15, finalY);
            doc.text('Received By:', pageWidth - 60, finalY);

            finalY += 15;
            doc.line(15, finalY, 65, finalY); // Line for Delivered By
            doc.line(pageWidth - 60, finalY, pageWidth - 10, finalY); // Line for Received By

            finalY += 5;
            doc.setFont(undefined, 'normal');
            doc.text('Name:', 15, finalY);
            doc.text('Name:', pageWidth - 60, finalY);

            finalY += 10;
            doc.line(15, finalY + 2, 65, finalY + 2); // Line for Name
            doc.line(pageWidth - 60, finalY + 2, pageWidth - 10, finalY + 2); // Line for Name

            finalY += 10;
            doc.text('Signature & Date:', 15, finalY);
            doc.text('Signature & Date:', pageWidth - 60, finalY);

            doc.save(`${dc.dcNumber}.pdf`);

        } catch (error) {
            console.error(error);
            Swal.fire('Error', 'Failed to generate PDF', 'error');
        }
    };


    const generateGatePass = async (dc) => {
        try {
            // Updated: Set Persistent State First
            await salesService.toggleGatePass(dc.id, true);

            const companyRes = await companyService.getProfile();
            const company = companyRes.data || {};

            const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
            // ... (rest of PDF generation remains same, but we need to ensure the state updates on UI) ...

            // Refetch to update UI state
            fetchDcs();

            // Re-use existing PDF generation code
            // (I will duplicate the logic or extract it if I could, but since replace_file cannot reuse code easily without refactor, 
            // I will implement the PDF generation block again here or reference a shared helper if I had one. 
            // Better strategy: Keep the PDF logic here as it was, just adding the API call at the start)

            const pageWidth = doc.internal.pageSize.width;
            let yPos = 15;

            // --- Header Section ---
            // Logo (Left)
            if (company.logo && company.logo.startsWith('data:image')) {
                try {
                    const format = company.logo.includes('image/jpeg') ? 'JPEG' : 'PNG';
                    doc.addImage(company.logo, format, 15, yPos, 35, 18);
                } catch (err) { console.error("Logo error:", err); }
            }

            // Distributor Logos (Right aligned - Added for Consistency)
            // Use DC's Quotation preference
            let activeLogos = [];
            try {
                const pref = dc.Quotation?.distributorLogo || 'default';
                let potentialLogos = [];

                if (company.distributorLogos) {
                    const parsed = JSON.parse(company.distributorLogos);
                    if (Array.isArray(parsed)) potentialLogos = parsed;
                }
                if (potentialLogos.length === 0 && company.distributorLogo) {
                    potentialLogos.push({ src: company.distributorLogo, active: true });
                }

                if (pref === 'none') {
                    activeLogos = [];
                } else if (pref === 'default' || !pref) {
                    activeLogos = potentialLogos.filter(l => l.active);
                } else if (pref.startsWith('logo_')) {
                    const index = parseInt(pref.replace('logo_', ''));
                    if (potentialLogos[index]) activeLogos = [potentialLogos[index]];
                }
            } catch (e) { console.error("Error parsing distributorLogos", e); }


            if (activeLogos.length > 0) {
                const logoWidth = 35;
                const logoHeight = 15;
                const spacing = 2;
                const rightMargin = pageWidth - 15;

                activeLogos.reverse().forEach((logo, index) => {
                    if (logo.src && logo.src.startsWith('data:image')) {
                        try {
                            const format = logo.src.includes('image/jpeg') ? 'JPEG' : 'PNG';
                            const xPos = rightMargin - ((index + 1) * logoWidth) - (index * spacing);
                            doc.addImage(logo.src, format, xPos, yPos, logoWidth, logoHeight);
                        } catch (err) { console.error("Logo render error", err); }
                    }
                });
            }

            // Company Name (Larger & Bold)
            doc.setFontSize(18); // Increased size
            doc.setFont(undefined, 'bold');
            doc.setTextColor(44, 62, 80); // Dark Blue/Grey
            doc.text(company.name || 'World Wide Vision Pvt Ltd', pageWidth / 2, yPos + 8, { align: 'center' });

            // Address (Centered below name)
            doc.setFontSize(9);
            doc.setFont(undefined, 'normal');
            doc.setTextColor(100); // Dark Grey

            let addrY = yPos + 14;

            const contactLine = [
                company.phone ? `Tel: ${company.phone}` : '',
                company.email ? `Email: ${company.email}` : ''
            ].filter(Boolean).join(' | ');

            doc.text(company.address || '', pageWidth / 2, addrY, { align: 'center' });
            addrY += 5;
            doc.text(contactLine, pageWidth / 2, addrY, { align: 'center' });
            addrY += 4;

            yPos = Math.max(yPos + 25, addrY + 10);

            // --- Title ---
            doc.setFontSize(18);
            doc.setFont(undefined, 'bold');
            doc.setTextColor(0);
            doc.text('GATE PASS', pageWidth / 2, yPos, { align: 'center' });
            doc.setLineWidth(0.5);
            doc.line(pageWidth / 2 - 25, yPos + 2, pageWidth / 2 + 25, yPos + 2);

            yPos += 15;

            // --- Info Tables (Side by Side) ---
            // Left: Organization/User Info
            const leftData = [
                ['Organization:', dc.Quotation?.Organization?.name || dc.Quotation?.organization || '-'],
                ['Receiver:', dc.receiverName || '-'],
                ['Vehicle No:', '__________________'],
                ['Driver Name:', '__________________']
            ];

            autoTable(doc, {
                startY: yPos,
                body: leftData,
                theme: 'plain',
                tableWidth: 95,
                margin: { left: 15 },
                styles: { fontSize: 10, cellPadding: 1.5, lineColor: 0, lineWidth: 0.1 },
                columnStyles: { 0: { fontStyle: 'bold', cellWidth: 30 } }
            });

            // Right: Date/DC Info
            const rightData = [
                ['Date:', dc.deliveryDate],
                ['Gate Pass No:', `GP-${dc.dcNumber.split('-').pop()}`], // Fake GP Number derived from DC
                ['Ref DC No:', dc.dcNumber],
                ['Time Out:', '__________________']
            ];

            autoTable(doc, {
                startY: yPos,
                body: rightData,
                theme: 'plain',
                tableWidth: 80,
                margin: { left: pageWidth - 95 },
                styles: { fontSize: 10, cellPadding: 1.5, lineColor: 0, lineWidth: 0.1 },
                columnStyles: { 0: { fontStyle: 'bold', cellWidth: 25 } }
            });

            yPos = doc.lastAutoTable.finalY + 10;

            // --- Items Table ---
            const itemRows = [];
            if (dc.DeliveryChallanItems) {
                dc.DeliveryChallanItems.forEach((item, index) => {
                    itemRows.push([
                        index + 1,
                        item.description || item.QuotationItem?.description || '-',
                        item.quantity + ' ' + (item.unit || 'Nos'),
                        ''
                    ]);
                });
            }

            autoTable(doc, {
                startY: yPos,
                head: [['Sr.', 'Description of Material', 'Quantity', 'Remarks']],
                body: itemRows,
                theme: 'grid',
                headStyles: {
                    fillColor: [44, 62, 80], // Consistent Dark Blue
                    textColor: 255,
                    fontStyle: 'bold',
                    halign: 'center'
                },
                styles: {
                    fontSize: 10,
                    valign: 'middle',
                    lineColor: 200,
                    lineWidth: 0.1
                },
                columnStyles: {
                    0: { cellWidth: 15, halign: 'center' },
                    2: { cellWidth: 30, halign: 'center' },
                    3: { cellWidth: 35 }
                }
            });

            // --- Footer Signatures ---
            const footerY = doc.internal.pageSize.height - 40;

            doc.setFontSize(10);
            doc.setFont(undefined, 'bold');
            doc.text('Prepared By', 30, footerY);
            doc.text('Authorized By', pageWidth / 2, footerY, { align: 'center' });
            doc.text('Security Check', pageWidth - 30, footerY, { align: 'right' });

            doc.setLineWidth(0.3);
            doc.line(15, footerY - 10, 60, footerY - 10);
            doc.line(pageWidth / 2 - 25, footerY - 10, pageWidth / 2 + 25, footerY - 10);
            doc.line(pageWidth - 60, footerY - 10, pageWidth - 15, footerY - 10);

            doc.save(`GatePass-${dc.dcNumber}.pdf`);

        } catch (error) {
            console.error("Gate Pass Error:", error);
            if (error.response && error.response.data) {
                console.error("Server Error Details:", error.response.data); // Log actual server error
                Swal.fire('Error', `Failed: ${error.response.data.error || 'Unknown Server Error'}`, 'error');
            } else {
                Swal.fire('Error', 'Failed to generate Gate Pass', 'error');
            }
        }
    };

    const handleDeleteGatePass = async (dc) => {
        const result = await Swal.fire({
            title: 'Delete Gate Pass?',
            text: "This will revoke the generated status.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                await salesService.toggleGatePass(dc.id, false); // Set to false
                fetchDcs(); // Refresh UI
                Swal.fire('Deleted!', 'Gate Pass status revoked.', 'success');
            } catch (error) {
                console.error("Gate Pass Delete Error:", error);
                Swal.fire('Error', 'Failed to delete Gate Pass', 'error');
            }
        }
    };

    const StatusBadge = ({ status }) => {
        const color = status === 'Delivered' ? 'success' : 'warning';
        return <span className={`badge bg-${color}`}>{status}</span>;
    };

    const filteredDCs = dcs.filter(dc =>
        !searchTerm ||
        (dc.dcNumber && dc.dcNumber.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (dc.receiverName && dc.receiverName.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (dc.Quotation && dc.Quotation.quoteNo && dc.Quotation.quoteNo.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (dc.Project && dc.Project.title && dc.Project.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (dc.location && dc.location.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    return (
        <div>
            {/* Header */}
            <div className="d-flex justify-content-between align-items-start mb-4">
                <div>
                    <h2 className="mb-1" style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        fontWeight: '700',
                        fontSize: '2rem'
                    }}>
                        Delivery Challans
                    </h2>
                    <p className="text-muted mb-0">Manage delivery challans and stock deductions</p>
                </div>
                <button className="btn btn-primary" onClick={handleCreate}>
                    <i className="bi bi-plus-lg me-1"></i> Create Challan
                </button>
            </div>

            <div className="mb-3">
                <div className="input-group">
                    <span className="input-group-text bg-white"><i className="bi bi-search"></i></span>
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Search DC by Number, Receiver, Project, or Quote Ref..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            {/* Content */}
            <div className="card shadow-sm">
                <div className="card-body">
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead>
                                <tr>
                                    <th>DC Number</th>
                                    <th>Date</th>
                                    <th>Quotation</th>
                                    <th>Project</th>
                                    <th>Receiver</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                    <th>Gate Pass</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan="8" className="text-center">Loading...</td></tr>
                                ) : dcs.length === 0 ? (
                                    <tr><td colSpan="8" className="text-center">No Delivery Challans found</td></tr>
                                ) : (
                                    filteredDCs.map(dc => (
                                        <tr key={dc.id}>
                                            <td><strong>{dc.dcNumber}</strong></td>
                                            <td>{dc.deliveryDate}</td>
                                            <td>{dc.Quotation?.quoteNo}</td>
                                            <td>{dc.Project?.title}</td>
                                            <td>{dc.receiverName}</td>
                                            <td><StatusBadge status={dc.status} /></td>
                                            <td>
                                                <div className="d-flex gap-1">
                                                    <button className="btn btn-sm btn-outline-warning" onClick={() => handleStatusClick(dc)} title="Update Status">
                                                        <i className="bi bi-arrow-repeat"></i>
                                                    </button>
                                                    <button className="btn btn-sm btn-outline-primary" onClick={() => handleEdit(dc)} title="Edit">
                                                        <i className="bi bi-pencil"></i>
                                                    </button>
                                                    <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(dc.id)} title="Delete">
                                                        <i className="bi bi-trash"></i>
                                                    </button>
                                                    <button className="btn btn-sm btn-outline-dark" onClick={() => generatePDF(dc)} title="Print DC">
                                                        <i className="bi bi-file-pdf"></i>
                                                    </button>
                                                </div>
                                            </td>
                                            <td>
                                                {dc.gatePassGenerated ? (
                                                    <div className="d-flex gap-1">
                                                        <button className="btn btn-sm btn-outline-dark" onClick={() => generateGatePass(dc)} title="Reprint Gate Pass">
                                                            <i className="bi bi-printer"></i>
                                                        </button>
                                                        <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteGatePass(dc)} title="Delete Gate Pass">
                                                            <i className="bi bi-trash"></i>
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <button className="btn btn-sm btn-outline-success" onClick={() => generateGatePass(dc)}>
                                                        <i className="bi bi-file-earmark-text me-1"></i> Generate
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Status Update Modal */}
            {showStatusModal && createPortal(
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
                    <div className="modal-dialog modal-sm modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Update Status</h5>
                                <button type="button" className="btn-close" onClick={() => setShowStatusModal(false)}><i className="bi bi-x-lg"></i></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Select Status</label>
                                    <select className="form-select" value={newStatus} onChange={(e) => setNewStatus(e.target.value)}>
                                        <option value="Pending">Pending</option>
                                        <option value="Delivered">Delivered</option>
                                        <option value="Returned">Returned</option>
                                        <option value="Cancelled">Cancelled</option>
                                    </select>
                                </div>
                                <div className="d-flex justify-content-end">
                                    <button className="btn btn-secondary me-2" onClick={() => setShowStatusModal(false)}>Cancel</button>
                                    <button className="btn btn-primary" onClick={saveStatusUpdate}>Update</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>, document.body
            )}

            {/* Create/Edit Modal */}
            {showModal && createPortal(
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000 }}>
                    <div className="modal-dialog modal-lg modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">{editingDC ? 'Edit Delivery Challan' : 'Create Delivery Challan'}</h5>
                                <button type="button" className="btn-close" onClick={() => { setShowModal(false); setEditingDC(null); }}><i className="bi bi-x-lg"></i></button>
                            </div>
                            <div className="modal-body">
                                <form onSubmit={handleSubmit}>
                                    <div className="row g-3">
                                        <div className="col-md-6">
                                            <label className="form-label">Select Awarded Quotation</label>
                                            <select
                                                className="form-select"
                                                value={formData.quotationId}
                                                onChange={handleQuoteSelect}
                                                required
                                                disabled={!!editingDC}
                                            >
                                                <option value="">Select Quotation</option>
                                                {quotations.map(q => (
                                                    <option key={q.id} value={q.id}>{q.quoteNo} - {q.title} ({q.organization})</option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="col-md-6">
                                            <label className="form-label">Delivery Date</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={formData.deliveryDate}
                                                onChange={e => setFormData({ ...formData, deliveryDate: e.target.value })}
                                                required
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">Receiver Name</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={formData.receiverName}
                                                onChange={e => setFormData({ ...formData, receiverName: e.target.value })}
                                                required
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">Location</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={formData.location}
                                                onChange={e => setFormData({ ...formData, location: e.target.value })}
                                            />
                                        </div>
                                    </div>

                                    {selectedQuote && (
                                        <div className="mt-4">
                                            <h6 className="border-bottom pb-2">Select Items to Deliver</h6>
                                            <div className="table-responsive">
                                                <table className="table table-sm table-bordered">
                                                    <thead className="table-light">
                                                        <tr>
                                                            <th>Item</th>
                                                            <th>Model</th>
                                                            <th className="text-center">Quoted / Rem</th>
                                                            <th>Warehouse</th>
                                                            <th className="text-center" style={{ width: '120px' }}>Deliver Now</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {quoteItems.map(item => (
                                                            <tr key={item.id}>
                                                                <td>{item.description}</td>
                                                                <td>{item.modelNumber}</td>
                                                                <td>
                                                                    {item.remainingQty !== undefined ? (
                                                                        <span>{item.quantity} / <strong>{item.remainingQty}</strong></span>
                                                                    ) : item.quantity}
                                                                </td>
                                                                <td>
                                                                    <select
                                                                        className={`form-select form-select-sm ${errors[item.id] ? 'is-invalid' : ''}`}
                                                                        value={item.selectedWarehouse || ''}
                                                                        onChange={(e) => {
                                                                            handleWarehouseChange(item.id, e.target.value);
                                                                            if (errors[item.id]) {
                                                                                setErrors(prev => ({ ...prev, [item.id]: null }));
                                                                            }
                                                                        }}
                                                                    >
                                                                        <option value="">Select Warehouse</option>
                                                                        {warehouses.map(wh => {
                                                                            const stock = item.Product?.ProductStocks?.find(s => s.WarehouseId === wh.id);
                                                                            const qty = stock ? stock.quantity : 0;
                                                                            return (
                                                                                <option key={wh.id} value={wh.id}>
                                                                                    {wh.name} ({qty})
                                                                                </option>
                                                                            );
                                                                        })}
                                                                    </select>
                                                                    {errors[item.id] && <div className="invalid-feedback">{errors[item.id]}</div>}
                                                                </td>
                                                                <td className="text-center">
                                                                    <input
                                                                        type="text"
                                                                        min="0"
                                                                        onInput={(e) => e.target.value = e.target.value.replace(/[^0-9]/g, '')}
                                                                        className={`form-control form-control-sm ${errors[item.id] || item.deliverQty > (item.remainingQty !== undefined ? item.remainingQty : item.quantity) ? 'is-invalid' : ''}`}
                                                                        value={item.deliverQty}
                                                                        onChange={e => handleQtyChange(item.id, e.target.value)}
                                                                    />
                                                                    {item.deliverQty > (item.remainingQty !== undefined ? item.remainingQty : item.quantity) && (
                                                                        <div className="invalid-feedback" style={{ fontSize: '0.7em', display: 'block' }}>
                                                                            Max: {item.remainingQty !== undefined ? item.remainingQty : item.quantity}
                                                                        </div>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                            <small className="text-danger">* Entering quantity greater than available stock will cause error.</small>
                                        </div>
                                    )}

                                    <div className="d-flex justify-content-end mt-4">
                                        <button type="button" className="btn btn-secondary me-2" onClick={() => { setShowModal(false); setEditingDC(null); }}>Cancel</button>
                                        <button type="submit" className="btn btn-primary">{editingDC ? 'Update Challan' : 'Create Challan'}</button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div >
                </div >,
                document.body
            )}
        </div >
    );
};

export default DeliveryChallanManagement;
